<template>
  <div class="container-fluid px-4 py-4" style="background:#f8fafc;min-height:100vh;">

    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between flex-wrap mb-4" style="gap:10px;">
      <div>
        <h5 class="text-dark mb-0" style="font-size:21px;font-weight:600;">Cash Shift Control</h5>
        <p class="text-secondary mb-0" style="font-size:12px;">Open drawer, run operations, then close with counted cash and variance.</p>
      </div>
      <div class="d-flex align-items-center" style="gap:10px;">
        <span
          class="badge"
          :class="active ? 'badge-success' : 'badge-secondary'"
          style="font-size:9px;letter-spacing:.5px;padding:3px 8px;border-radius:20px;"
        >{{ active ? 'ACTIVE SHIFT' : 'NO ACTIVE SHIFT' }}</span>
        <button v-if="active" class="btn btn-primary btn-sm" @click="closeSession">Close Shift</button>
        <button v-else class="btn btn-primary btn-sm" @click="openSession">Open Cash Session</button>
      </div>
    </div>

    <!-- Current shift stats -->
    <div v-if="loadingActive" class="mb-4 text-center py-3">
      <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
    </div>

    <div v-else-if="!active" class="text-secondary mb-4 pb-3" style="font-size:13px;border-bottom:1px solid #eef0f2;">
      No cash session is open right now.
    </div>

    <div v-else class="row mb-4 pb-2 kpi-row">
      <div class="col-6 col-md-4 kpi-col">
        <div class="text-secondary mb-1" style="font-size:12px;">Opened</div>
        <div class="text-dark font-weight-bold" style="font-size:17px;">{{ formatDateTime(active.opened_at) }}</div>
      </div>
      <div class="col-6 col-md-4 kpi-col">
        <div class="text-secondary mb-1" style="font-size:12px;">Cash Sales</div>
        <div class="text-dark font-weight-bold" style="font-size:17px;">{{ formatCurrency(active.breakdown.cash_sales) }}</div>
      </div>
      <div class="col-6 col-md-4 kpi-col">
        <div class="text-secondary mb-1" style="font-size:12px;">Expected Now</div>
        <div class="text-dark font-weight-bold" style="font-size:17px;">{{ formatCurrency(active.breakdown.expected_cash) }}</div>
      </div>
      <div class="col-6 col-md-4 kpi-col mt-3">
        <div class="text-secondary mb-1" style="font-size:12px;">Opening Float</div>
        <div class="text-dark font-weight-bold" style="font-size:17px;">{{ formatCurrency(active.opening_amount) }}</div>
      </div>
      <div class="col-6 col-md-4 kpi-col mt-3">
        <div class="text-secondary mb-1" style="font-size:12px;">Other Cash Received</div>
        <div class="text-dark font-weight-bold" style="font-size:17px;">{{ formatCurrency(active.breakdown.other_received) }}</div>
      </div>
      <div class="col-6 col-md-4 kpi-col mt-3">
        <div class="text-secondary mb-1" style="font-size:12px;">Cash Expenses + Refunds</div>
        <div class="text-dark font-weight-bold" style="font-size:17px;">{{ formatCurrency(active.breakdown.cash_expenses + active.breakdown.cash_refunds) }}</div>
      </div>
    </div>

    <!-- Shift Audit -->
    <div v-if="canAudit" class="bg-white border" style="border-radius:12px;border-color:#e9ecef !important;padding:1.25rem 1.5rem;">
      <div class="d-flex align-items-start justify-content-between flex-wrap mb-3" style="gap:10px;">
        <div>
          <h6 class="text-dark mb-0" style="font-size:14px;font-weight:600;">Shift Audit</h6>
          <p class="text-secondary mb-0" style="font-size:12px;">Review opening, expected close, actual close, and variance by cashier.</p>
        </div>
        <button class="btn btn-primary btn-sm" @click="exportCsv">
          <i class="bi bi-download mr-1"></i> Export CSV
        </button>
      </div>

      <div v-if="metrics" class="row mb-4 pb-2">
        <div class="col-6 col-md-3 kpi-divider">
          <div class="text-secondary mb-1" style="font-size:12px;">Pending Approval</div>
          <div class="text-dark font-weight-bold" style="font-size:17px;">{{ metrics.pending_shift_closings }}</div>
        </div>
        <div class="col-6 col-md-3 kpi-divider">
          <div class="text-secondary mb-1" style="font-size:12px;">Approved Today</div>
          <div class="text-dark font-weight-bold" style="font-size:17px;">{{ metrics.approved_closings_today }}</div>
        </div>
        <div class="col-6 col-md-3 kpi-divider">
          <div class="text-secondary mb-1" style="font-size:12px;">Today's Variance</div>
          <div class="font-weight-bold" :class="metrics.today_cash_variance < 0 ? 'text-danger' : 'text-dark'" style="font-size:17px;">{{ formatCurrency(metrics.today_cash_variance) }}</div>
        </div>
        <div class="col-6 col-md-3">
          <div class="text-secondary mb-1" style="font-size:12px;">Month Variance</div>
          <div class="font-weight-bold" :class="metrics.month_cash_variance < 0 ? 'text-danger' : 'text-dark'" style="font-size:17px;">{{ formatCurrency(metrics.month_cash_variance) }}</div>
        </div>
      </div>

      <div class="row align-items-end mb-3">
        <div class="col-12 col-md-4 mb-2">
          <label class="text-secondary d-block mb-1" style="font-size:11px;">Date Range</label>
          <date-range-picker :auto-apply="true" :ranges="false" opens="right" v-model="dateRange" @update="fetchAudit" class="w-100">
            <template #input="picker">
              <span style="font-size:12px;">
                <i class="bi bi-calendar-event mr-1"></i>
                <template v-if="picker.startDate">{{ formatDate(picker.startDate) }} - {{ formatDate(picker.endDate) }}</template>
                <template v-else>Filter by Date</template>
              </span>
            </template>
          </date-range-picker>
        </div>
        <div class="col-6 col-md-2 mb-2">
          <label class="text-secondary d-block mb-1" style="font-size:11px;">Cashier</label>
          <select class="form-control form-control-sm" v-model="filters.cashier_id">
            <option :value="null">All</option>
            <option v-for="c in cashierOptions" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="col-6 col-md-2 mb-2">
          <label class="text-secondary d-block mb-1" style="font-size:11px;">Status</label>
          <select class="form-control form-control-sm" v-model="filters.status">
            <option :value="null">All</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div class="col-6 col-md-2 mb-2">
          <label class="text-secondary d-block mb-1" style="font-size:11px;">Variance</label>
          <select class="form-control form-control-sm" v-model="filters.variance">
            <option :value="null">All</option>
            <option value="over">Over</option>
            <option value="short">Short</option>
            <option value="exact">Exact</option>
          </select>
        </div>
        <div class="col-6 col-md-2 mb-2 d-flex" style="gap:8px;">
          <button class="btn btn-primary btn-sm flex-fill" @click="fetchAudit(1)">Filter</button>
          <button class="btn btn-light border btn-sm flex-fill" @click="resetFilters">Reset</button>
        </div>
      </div>

      <div v-if="loadingAudit" class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
      </div>

      <div v-else class="table-responsive">
        <b-table hover striped responsive :items="rows" :fields="fields" @row-clicked="openDetail">
          <template #cell(opening_amount)="d">{{ formatCurrency(d.item.opening_amount) }}</template>
          <template #cell(cash_sales)="d">{{ formatCurrency(d.item.cash_sales) }}</template>
          <template #cell(other_received)="d">{{ formatCurrency(d.item.other_received) }}</template>
          <template #cell(cash_expenses)="d">{{ formatCurrency(d.item.cash_expenses) }}</template>
          <template #cell(expected_cash)="d">{{ formatCurrency(d.item.expected_cash) }}</template>
          <template #cell(closing_amount)="d">{{ d.item.closing_amount != null ? formatCurrency(d.item.closing_amount) : '—' }}</template>
          <template #cell(variance)="d">
            <span :class="d.item.variance == null ? '' : (d.item.variance < 0 ? 'text-danger' : d.item.variance > 0 ? 'text-dark font-weight-bold' : '')">
              {{ d.item.variance != null ? formatCurrency(d.item.variance) : '—' }}
            </span>
          </template>
          <template #cell(status)="d">
            <span class="badge" :class="d.item.status === 'open' ? 'badge-success' : 'badge-secondary'">{{ d.item.status.toUpperCase() }}</span>
          </template>
          <template #cell(actions)="d">
            <div class="dropdown" @click.stop>
              <a href="#" data-toggle="dropdown" aria-expanded="false" class="btn btn-sm btn-link p-0">
                <i class="bi bi-three-dots-vertical"></i>
              </a>
              <div class="dropdown-menu dropdown-menu-right">
                <a class="dropdown-item" href="#" @click.prevent="openDetail(d.item)">{{ d.item.status === 'open' ? 'View / Close' : 'View' }}</a>
                <template v-if="d.item.approval_status === 'pending' && can('approve_shift_cash_closing')">
                  <a class="dropdown-item" href="#" @click.prevent="approve(d.item)">Approve</a>
                  <a class="dropdown-item text-danger" href="#" @click.prevent="openReject(d.item)">Reject</a>
                </template>
              </div>
            </div>
          </template>
        </b-table>
      </div>

      <div v-if="!loadingAudit && rows.length === 0" class="text-center text-secondary py-4" style="font-size:12px;">
        No shifts found for these filters.
      </div>
    </div>

    <!-- Row detail -->
    <b-modal v-model="detailOpen" hide-footer centered size="lg" title="Cash Shift Detail">
      <div v-if="detail" style="font-size:13px;">

        <!-- Header -->
        <div class="d-flex justify-content-between align-items-start mb-3 pb-3" style="border-bottom:1px solid #eef0f2;">
          <div>
            <div class="text-dark font-weight-bold" style="font-size:15px;">{{ detail.cashier }}</div>
            <div class="text-secondary" style="font-size:12px;">{{ detail.business_date }} · Opened {{ formatDateTime(detail.opened_at) }}</div>
          </div>
          <span class="badge" :class="detail.status === 'open' ? 'badge-success' : 'badge-secondary'" style="font-size:9px;letter-spacing:.5px;padding:3px 8px;border-radius:20px;">{{ detail.status.toUpperCase() }}</span>
        </div>

        <!-- Summary strip -->
        <div class="row mb-4 pb-3" style="border-bottom:1px solid #eef0f2;">
          <div class="col-6 col-md-3 kpi-divider">
            <div class="text-secondary mb-1" style="font-size:11px;">Opening Float</div>
            <div class="text-dark font-weight-bold" style="font-size:15px;">{{ formatCurrency(detail.opening_amount) }}</div>
          </div>
          <div class="col-6 col-md-3 kpi-divider">
            <div class="text-secondary mb-1" style="font-size:11px;">Cash Sales</div>
            <div class="text-dark font-weight-bold" style="font-size:15px;">{{ formatCurrency(detail.cash_sales) }}</div>
          </div>
          <div class="col-6 col-md-3 kpi-divider">
            <div class="text-secondary mb-1" style="font-size:11px;">Other Received</div>
            <div class="text-dark font-weight-bold" style="font-size:15px;">{{ formatCurrency(detail.other_received) }}</div>
          </div>
          <div class="col-6 col-md-3">
            <div class="text-secondary mb-1" style="font-size:11px;">Expected Now</div>
            <div class="text-dark font-weight-bold" style="font-size:15px;">{{ formatCurrency(detail.expected_cash) }}</div>
          </div>
        </div>

        <!-- Settlement -->
        <div class="mb-4 pb-3" style="border-bottom:1px solid #eef0f2;">
          <h6 class="text-dark mb-2" style="font-size:13px;font-weight:600;">Settlement</h6>
          <div class="d-flex justify-content-between mb-1"><span class="text-secondary">Status</span><span class="font-weight-bold">{{ detail.status === 'open' ? 'Open' : (detail.approval_status || 'Closed') }}</span></div>
          <div class="d-flex justify-content-between mb-1"><span class="text-secondary">Expected</span><span>{{ formatCurrency(detail.expected_cash) }}</span></div>
          <div class="d-flex justify-content-between mb-1"><span class="text-secondary">Actual (Counted)</span><span>{{ detail.closing_amount != null ? formatCurrency(detail.closing_amount) : '—' }}</span></div>
          <div class="d-flex justify-content-between">
            <span class="text-secondary">Variance</span>
            <span class="font-weight-bold" :class="detail.variance == null ? '' : (detail.variance < 0 ? 'text-danger' : '')">{{ detail.variance != null ? formatCurrency(detail.variance) : '—' }}</span>
          </div>
          <div v-if="detail.approved_by" class="text-secondary mt-2" style="font-size:12px;">{{ detail.approval_status }} by {{ detail.approved_by }} on {{ detail.approved_at }}</div>
          <div v-if="detail.rejection_reason" class="text-danger mt-1" style="font-size:12px;">Reason: {{ detail.rejection_reason }}</div>
        </div>

        <!-- Transaction drill-down -->
        <div class="mb-4 pb-3" style="border-bottom:1px solid #eef0f2;">
          <div class="d-flex justify-content-between align-items-baseline mb-2">
            <h6 class="text-dark mb-0" style="font-size:13px;font-weight:600;">Transactions</h6>
            <span class="text-secondary" style="font-size:11px;">{{ detail.window_from }} → {{ detail.window_to }}</span>
          </div>
          <div v-if="!detail.transactions || detail.transactions.length === 0" class="text-secondary" style="font-size:12px;">
            No cash transactions in this window.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-sm mb-0" style="font-size:12px;">
              <thead>
                <tr class="text-secondary">
                  <th style="font-weight:500;">Time</th>
                  <th style="font-weight:500;">Type</th>
                  <th style="font-weight:500;">Reference</th>
                  <th style="font-weight:500;" class="text-right">Cash In</th>
                  <th style="font-weight:500;" class="text-right">Cash Out</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(t, i) in detail.transactions" :key="i">
                  <td>{{ t.time }}</td>
                  <td>{{ t.type }}</td>
                  <td>{{ t.reference }}</td>
                  <td class="text-right">{{ t.cash_in != null ? formatCurrency(t.cash_in) : '—' }}</td>
                  <td class="text-right">{{ t.cash_out != null ? formatCurrency(t.cash_out) : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Notes -->
        <div class="row mb-4">
          <div class="col-6">
            <div class="text-secondary mb-1" style="font-size:11px;">Opening Note</div>
            <div class="text-dark" style="font-size:12px;">{{ detail.notes || '—' }}</div>
          </div>
          <div class="col-6">
            <div class="text-secondary mb-1" style="font-size:11px;">Closing Note</div>
            <div class="text-dark" style="font-size:12px;">{{ detail.closing_notes || '—' }}</div>
          </div>
        </div>

        <!-- Approve / Reject -->
        <div v-if="detail.approval_status === 'pending' && can('approve_shift_cash_closing')" class="d-flex justify-content-end mb-2" style="gap:8px;">
          <button class="btn btn-light border btn-sm text-danger" @click="openReject(detail)">Reject</button>
          <button class="btn btn-primary btn-sm" @click="approve(detail)">Approve</button>
        </div>

        <!-- Close shift -->
        <div v-if="detail.status === 'open'" class="pt-3" style="border-top:1px solid #eef0f2;">
          <h6 class="text-dark mb-1" style="font-size:13px;font-weight:600;">Close Shift</h6>
          <p class="text-secondary mb-2" style="font-size:12px;">Confirm counted cash to close this shift.</p>
          <div class="form-group mb-2">
            <label class="text-secondary" style="font-size:11px;">Counted Cash in Drawer</label>
            <input type="number" min="0" step="0.01" class="form-control form-control-sm" v-model.number="closeForm.closing_amount" placeholder="0.00">
          </div>
          <div class="form-group mb-2">
            <label class="text-secondary" style="font-size:11px;">Closing Note (optional)</label>
            <input type="text" class="form-control form-control-sm" v-model="closeForm.notes">
          </div>
          <div class="text-right">
            <button class="btn btn-primary btn-sm" :disabled="closeSubmitting" @click="submitClose(detail)">
              {{ closeSubmitting ? 'Closing…' : 'Confirm Close Shift' }}
            </button>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Reject reason -->
    <b-modal v-model="rejectOpen" hide-footer centered title="Reject Closing">
      <form @submit.prevent="submitReject" style="font-size:13px;">
        <div class="form-group">
          <label>Reason <span class="text-danger">*</span></label>
          <textarea class="form-control" v-model="rejectReason" rows="3" required></textarea>
        </div>
        <div v-if="rejectError" class="alert alert-danger py-2" style="font-size:12px;">{{ rejectError }}</div>
        <div class="text-right">
          <button type="submit" class="btn btn-primary" :disabled="rejectSubmitting">{{ rejectSubmitting ? 'Submitting…' : 'Reject' }}</button>
        </div>
      </form>
    </b-modal>

  </div>
</template>

<script>
import { downloadFile } from '../../services/pdfDownload';
import axios from "axios";
import moment from "moment";
import DateRangePicker from "vue2-daterange-picker/src/components/DateRangePicker";

export default {
  name: "CashShiftControl",
  components: { DateRangePicker },
  data() {
    return {
      active: null,
      loadingActive: true,
      rows: [],
      loadingAudit: false,
      detail: null,
      detailOpen: false,
      closeForm: { closing_amount: null, notes: "" },
      closeSubmitting: false,
      metrics: null,
      rejectTarget: null,
      rejectOpen: false,
      rejectReason: "",
      rejectSubmitting: false,
      rejectError: null,
      dateRange: { startDate: null, endDate: null },
      filters: { cashier_id: null, status: null, variance: null },
      fields: [
        { key: "business_date", label: "Date", sortable: true },
        { key: "cashier", label: "Cashier", sortable: true },
        { key: "opened_at", label: "Opened" },
        { key: "closed_at", label: "Closed" },
        { key: "opening_amount", label: "Opening" },
        { key: "cash_sales", label: "Cash Sales" },
        { key: "other_received", label: "Other Received" },
        { key: "cash_expenses", label: "Expenses + Refunds" },
        { key: "expected_cash", label: "Expected" },
        { key: "closing_amount", label: "Actual" },
        { key: "variance", label: "Variance" },
        { key: "status", label: "Status" },
        { key: "actions", label: "", class: "text-right" },
      ],
    };
  },
  computed: {
    canAudit() {
      return this.can("can_close_shift_cash") || this.can("manage-employees") || this.can("can manage users");
    },
    cashierOptions() {
      const seen = new Map();
      this.rows.forEach((r) => seen.set(r.cashier_id, r.cashier));
      return Array.from(seen, ([id, name]) => ({ id, name }));
    },
  },
  watch: {
    "$store.state.cashSession"() {
      this.fetchActive();
    },
  },
  methods: {
    fetchActive() {
      this.loadingActive = true;
      axios.get("/api/cash-sessions/active").then((res) => {
        const data = res.data;
        // Defensive: the backend always includes breakdown when a session
        // exists, but a stale cached/served response (CDN, old bundle
        // talking to a since-migrated API, etc.) shouldn't be able to crash
        // this page — fall back to a zeroed breakdown instead.
        if (data && !data.breakdown) {
          data.breakdown = {
            opening_amount: data.opening_amount ?? 0,
            cash_sales: 0,
            other_received: 0,
            cash_expenses: 0,
            cash_refunds: 0,
            expected_cash: data.opening_amount ?? 0,
          };
        }
        this.active = data;
      }).catch(() => {
        this.active = null;
      }).finally(() => { this.loadingActive = false; });
    },
    fetchAudit(page) {
      if (!this.canAudit) return;
      this.loadingAudit = true;
      axios.get("/api/cash-sessions", {
        params: {
          start_date: this.dateRange.startDate ? moment(this.dateRange.startDate).format("YYYY-MM-DD") : null,
          end_date: this.dateRange.endDate ? moment(this.dateRange.endDate).format("YYYY-MM-DD") : null,
          cashier_id: this.filters.cashier_id,
          status: this.filters.status,
          variance: this.filters.variance,
          page: typeof page === "number" ? page : 1,
        },
      }).then((res) => {
        this.rows = res.data.data;
      }).finally(() => { this.loadingAudit = false; });
    },
    resetFilters() {
      this.dateRange = { startDate: null, endDate: null };
      this.filters = { cashier_id: null, status: null, variance: null };
      this.fetchAudit(1);
    },
    openSession() {
      this.$root.$emit("cash-session-required", { retry: () => this.fetchActive(), cancel: () => {} });
    },
    closeSession() {
      this.$root.$emit("open-close-shift-modal");
    },
    submitClose(row) {
      this.closeSubmitting = true;
      axios.post(`/api/cash-sessions/${row.id}/close`, {
        closing_amount: this.closeForm.closing_amount,
        notes: this.closeForm.notes || null,
      }).then(() => {
        this.detailOpen = false;
        this.fetchAudit();
        this.fetchActive();
        this.$toasts.push({ type: "success", message: "Shift closed." });
      }).catch((err) => {
        this.$toasts.push({ type: "danger", message: err.response?.data?.message || "Failed to close shift." });
      }).finally(() => { this.closeSubmitting = false; });
    },
    openDetail(row) {
      this.closeForm = { closing_amount: null, notes: "" };
      axios.get(`/api/cash-sessions/${row.id}`).then((res) => {
        this.detail = res.data;
        this.detailOpen = true;
      });
    },
    fetchMetrics() {
      if (!this.canAudit) return;
      axios.get("/api/shift-cash-closings/metrics").then((res) => {
        this.metrics = res.data;
      });
    },
    approve(row) {
      axios.post(`/api/shift-cash-closings/${row.id}/approve`).then(() => {
        this.detailOpen = false;
        this.fetchAudit();
        this.fetchMetrics();
        this.$toasts.push({ type: "success", message: "Closing approved." });
      });
    },
    openReject(row) {
      this.rejectTarget = row;
      this.rejectReason = "";
      this.rejectError = null;
      this.rejectOpen = true;
    },
    submitReject() {
      this.rejectSubmitting = true;
      this.rejectError = null;
      axios.post(`/api/shift-cash-closings/${this.rejectTarget.id}/reject`, { reason: this.rejectReason })
        .then(() => {
          this.rejectOpen = false;
          this.detailOpen = false;
          this.fetchAudit();
          this.fetchMetrics();
          this.$toasts.push({ type: "success", message: "Closing rejected." });
        })
        .catch((err) => {
          this.rejectError = err.response?.data?.message || "Failed to reject.";
        })
        .finally(() => { this.rejectSubmitting = false; });
    },
    exportCsv() {
      const params = new URLSearchParams({
        start_date: this.dateRange.startDate ? moment(this.dateRange.startDate).format("YYYY-MM-DD") : "",
        end_date: this.dateRange.endDate ? moment(this.dateRange.endDate).format("YYYY-MM-DD") : "",
      });
      downloadFile(`/api/shift-cash-closings/export?${params.toString()}`, "shift-cash-closings.csv")
        .catch(() => this.$toasts.push({ type: "error", message: "Could not export. Please try again." }));
    },
    formatCurrency(v) {
      if (v == null) return "—";
      const currency = this.$store.state.userDetails?.company?.companysettings?.currency || "KES";
      const abs = Math.abs(v);
      const fmt = currency + " " + abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return v < 0 ? "-" + fmt : fmt;
    },
    formatDate(d) { return d ? moment(d).format("DD/MM/YYYY") : "—"; },
    formatDateTime(d) { return d ? moment(d).format("DD/MM/YYYY HH:mm") : "—"; },
  },
  mounted() {
    this.fetchActive();
    if (this.canAudit) {
      this.fetchAudit(1);
      this.fetchMetrics();
    }
  },
};
</script>

<style scoped>
/* Faint column separation, no card/border look — matches the rest of the
   metric row: text only, thin divider between columns, none at row ends. */
.kpi-col {
  border-right: 1px solid #eef0f2;
}
.kpi-col:nth-child(3n) {
  border-right: none;
}
.kpi-divider {
  border-right: 1px solid #eef0f2;
}
@media (max-width: 767px) {
  .kpi-col,
  .kpi-divider {
    border-right: none;
  }
}

/* The date-range-picker renders its own input-lookalike, not a real
   <input>, so it doesn't inherit .form-control-sm sizing — match it by
   hand to the selects/buttons beside it so the filter row lines up. */
::v-deep .reportrange-text {
  height: calc(1.5em + .5rem + 2px);
  padding: .25rem .5rem;
  font-size: .875rem;
  line-height: 1.5;
  border: 1px solid #ced4da;
  border-radius: .2rem;
  background: #fff;
  display: flex;
  align-items: center;
}
</style>
